import { Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';

/** Valid FHIR R4 ParticipationStatus values. */
const VALID_PARTICIPANT_STATUSES = ['accepted', 'declined', 'tentative', 'needs-action'] as const;

/** Appointment statuses that allow missing start/end (app-3). */
const NO_DATES_STATUSES = new Set(['proposed', 'cancelled', 'waitlist']);

/** Appointment statuses that allow cancelationReason (app-4). */
const CANCELATION_REASON_STATUSES = new Set(['cancelled', 'noshow']);

/**
 * FHIR R4 Appointment Validator
 *
 * Implements FHIR R4 Appointment constraints:
 *
 * - **app-1** (Rule on participant): Either the type or actor on the participant SHALL be specified
 * - **app-2** (Rule): Either start and end are specified, or neither
 * - **app-3** (Rule): Only proposed or cancelled appointments can be missing start/end dates
 * - **app-4** (Rule): cancelationReason is only used for appointments that have been cancelled or no-show
 * - Participant sub-element: `status` (1..1) with value-set conformance
 */
@Injectable({ providedIn: 'root' })
export class FhirAppointmentValidator {
  validate(resource: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const r = resource as Record<string, unknown>;

    // app-2: Either start and end are specified, or neither
    this.validateApp2(r, errors);

    // app-3: Only proposed/cancelled/waitlist can be missing start/end
    this.validateApp3(r, errors);

    // app-4: cancelationReason only for cancelled/noshow
    this.validateApp4(r, errors);

    // Participant validation (app-1 + required fields)
    this.validateParticipants(r, errors);

    // Informational
    if (!r['description']) {
      warnings.push('Appointment has no description');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  /** App-2: start.exists() = end.exists() */
  private validateApp2(r: Record<string, unknown>, errors: ConversionError[]): void {
    const hasStart = r['start'] !== undefined && r['start'] !== null;
    const hasEnd = r['end'] !== undefined && r['end'] !== null;

    if (hasStart !== hasEnd) {
      errors.push({
        code: ERROR_CODES.APP_2_START_END_BOTH,
        message: 'app-2: Either both start and end must be specified, or neither',
        severity: 'error',
      });
    }
  }

  /** App-3: (start.exists() and end.exists()) or (status in ('proposed' | 'cancelled' | 'waitlist')) */
  private validateApp3(r: Record<string, unknown>, errors: ConversionError[]): void {
    const hasStart = r['start'] !== undefined && r['start'] !== null;
    const hasEnd = r['end'] !== undefined && r['end'] !== null;
    const status = r['status'] as string | undefined;

    if ((!hasStart || !hasEnd) && status && !NO_DATES_STATUSES.has(status)) {
      errors.push({
        code: ERROR_CODES.APP_3_START_END_STATUS,
        message: `app-3: Appointments with status "${status}" must have start and end dates`,
        path: 'start',
        severity: 'error',
      });
    }
  }

  /** App-4: cancelationReason.exists() implies (status='no-show' or status='cancelled') */
  private validateApp4(r: Record<string, unknown>, errors: ConversionError[]): void {
    const cancelationReason = r['cancelationReason'];
    const status = r['status'] as string | undefined;

    if (cancelationReason && status && !CANCELATION_REASON_STATUSES.has(status)) {
      errors.push({
        code: ERROR_CODES.APP_4_CANCELATION_REASON_STATUS,
        message: `app-4: cancelationReason can only be specified when status is "cancelled" or "noshow", not "${status}"`,
        path: 'cancelationReason',
        severity: 'error',
      });
    }
  }

  /** Validate participants: app-1 (type or actor), status (1..1). */
  private validateParticipants(r: Record<string, unknown>, errors: ConversionError[]): void {
    const participants = r['participant'] as Record<string, unknown>[] | undefined;
    if (!participants || !Array.isArray(participants)) return;

    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];

      // app-1: type.exists() or actor.exists()
      const hasType = Array.isArray(p['type']) && (p['type'] as unknown[]).length > 0;
      const hasActor = p['actor'] !== undefined && p['actor'] !== null;

      if (!hasType && !hasActor) {
        errors.push({
          code: ERROR_CODES.APP_1_PARTICIPANT_TYPE_OR_ACTOR,
          message: `app-1: participant[${i}] must have either a type or an actor specified`,
          path: `participant[${i}]`,
          severity: 'error',
        });
      }

      // participant.status is required (1..1)
      const status = p['status'] as string | undefined;
      if (!status) {
        errors.push({
          code: ERROR_CODES.APP_PARTICIPANT_MISSING_STATUS,
          message: `participant[${i}].status is required`,
          path: `participant[${i}].status`,
          severity: 'error',
        });
      } else if (!VALID_PARTICIPANT_STATUSES.includes(status as (typeof VALID_PARTICIPANT_STATUSES)[number])) {
        errors.push({
          code: ERROR_CODES.APP_PARTICIPANT_INVALID_STATUS,
          message: `participant[${i}].status "${status}" is invalid. Must be one of: ${VALID_PARTICIPANT_STATUSES.join(', ')}`,
          path: `participant[${i}].status`,
          severity: 'error',
        });
      }
    }
  }
}
